import { useState } from "react"
import { useQuery } from "urql"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { TimeAgo } from "@/components/shared/time-ago"
import { JsonViewer } from "@/components/shared/json-viewer"
import { AuditLogsQuery } from "@/lib/graphql/queries"
import type { ResultOf } from "@/lib/graphql/types"

type AuditLogItem = ResultOf<typeof AuditLogsQuery>["auditLogs"]["nodes"][number]

const LIMIT = 25

const ACTOR_TYPES = [
  { value: "api_key", label: "API Key" },
  { value: "user", label: "User" },
  { value: "system", label: "System" },
] as const

const RESOURCE_TYPES = [
  { value: "source", label: "Source" },
  { value: "destination", label: "Destination" },
  { value: "subscription", label: "Subscription" },
  { value: "application", label: "Application" },
  { value: "endpoint", label: "Endpoint" },
  { value: "message", label: "Message" },
  { value: "api_key", label: "API Key" },
  { value: "alert_rule", label: "Alert Rule" },
  { value: "bookmark", label: "Bookmark" },
  { value: "event_type_schema", label: "Event Type Schema" },
  { value: "transformation", label: "Transformation" },
  { value: "portal_link", label: "Portal Link" },
  { value: "stream", label: "Stream" },
  { value: "organization", label: "Organization" },
  { value: "user", label: "User" },
] as const

const ALL_VALUE = "__all__"

function truncateId(id: string): string {
  if (id.length <= 12) return id
  return `${id.slice(0, 8)}...`
}

function formatAction(action: string): string {
  return action
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

export function AuditLogsPage() {
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)
  const [actorType, setActorType] = useState<string>(ALL_VALUE)
  const [resourceType, setResourceType] = useState<string>(ALL_VALUE)
  const [selectedLog, setSelectedLog] = useState<AuditLogItem | null>(null)

  const search = debouncedSearch || undefined
  const actorTypeFilter = actorType === ALL_VALUE ? undefined : actorType
  const resourceTypeFilter = resourceType === ALL_VALUE ? undefined : resourceType

  const pagination = useCursorPagination(LIMIT, [search, actorTypeFilter, resourceTypeFilter])

  const [result] = useQuery({
    query: AuditLogsQuery,
    variables: {
      search,
      actorType: actorTypeFilter,
      resourceType: resourceTypeFilter,
      after: pagination.after,
      first: pagination.first,
    },
  })

  const columns: Column<AuditLogItem>[] = [
    {
      header: "Time",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "Actor",
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-mono text-muted-foreground">{row.actorType}</span>
          {row.actorName && <span className="text-sm font-medium">{row.actorName}</span>}
        </div>
      ),
    },
    {
      header: "Action",
      cell: (row) => (
        <span className="text-sm font-medium">{formatAction(row.action)}</span>
      ),
    },
    {
      header: "Resource",
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-mono text-muted-foreground">{row.resourceType}</span>
          <code className="text-xs font-mono">{truncateId(row.resourceId)}</code>
        </div>
      ),
    },
    {
      header: "IP Address",
      cell: (row) => (
        <span className="text-xs font-mono text-muted-foreground">
          {row.ipAddress || "-"}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Audit Logs" description="View a record of all actions taken in the system" />

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Input
          placeholder="Search audit logs..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
        <Select value={actorType} onValueChange={setActorType}>
          <SelectTrigger className="w-35 text-sm">
            <SelectValue placeholder="Actor type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All actors</SelectItem>
            {ACTOR_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={resourceType} onValueChange={setResourceType}>
          <SelectTrigger className="w-45 text-sm">
            <SelectValue placeholder="Resource type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All resources</SelectItem>
            {RESOURCE_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={result.data?.auditLogs.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.auditLogs.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No audit logs"
        emptyDescription="Audit logs will appear here as actions are taken in the system"
        onRowClick={(row) => setSelectedLog(selectedLog?.id === row.id ? null : row)}
      />

      {selectedLog && (
        <div className="rounded-lg glass-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/20">
            <h3 className="text-sm font-semibold">Audit Log Details</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedLog(null)}
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <DetailField label="Actor Type" value={selectedLog.actorType} />
              <DetailField label="Actor ID" value={selectedLog.actorId} mono />
              <DetailField label="Actor Name" value={selectedLog.actorName || "N/A"} />
              <DetailField label="Action" value={formatAction(selectedLog.action)} />
              <DetailField label="Resource Type" value={selectedLog.resourceType} />
              <DetailField label="Resource ID" value={selectedLog.resourceId} mono />
              <DetailField label="IP Address" value={selectedLog.ipAddress || "N/A"} mono />
              <DetailField label="Timestamp" value={new Date(selectedLog.createdAt).toLocaleString()} />
            </div>
            {selectedLog.userAgent && (
              <div className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  User Agent
                </span>
                <p className="text-sm font-mono break-all text-foreground">{selectedLog.userAgent}</p>
              </div>
            )}
            {selectedLog.details && (
              <div className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Details
                </span>
                <JsonViewer data={selectedLog.details} collapsed />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function DetailField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="space-y-1">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        {label}
      </span>
      <p className={`text-sm ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  )
}
